(() => {
  const ROOT = window.Shopify?.routes?.root || "/";

  const SELECTORS = {
    drawer: "#CartDrawer, cart-drawer, .cart-drawer",
    // Nouvelle structure : div au lieu de tr
    row: '[id^="CartDrawer-Item-"].cart-item--normal',
    qtyInput: 'input.quantity__input[name="updates[]"][data-index]',
    titleLink: ".cart-item__name",
    subLabel: ".moodz-cart-card__options",
    // Zone d'injection native dans chaque carte
    switchZone: ".moodz-cart-card__switch-zone",
  };

  const PLAN_CACHE_BY_VARIANT = new Map();
  const PRODUCT_JSON_CACHE_BY_HANDLE = new Map();

  // -----------------------------
  // Helpers
  // -----------------------------
  const formatMoney = (cents) => {
    if (typeof cents !== "number" || !Number.isFinite(cents)) return "";
    const fmt =
      window.theme?.moneyFormat ||
      window.Shopify?.moneyFormat ||
      "{{amount_with_comma_separator}}€";

    if (window.Shopify?.formatMoney) {
      try {
        return window.Shopify.formatMoney(cents, fmt);
      } catch {}
    }
    const euros = (cents / 100).toFixed(2).replace(".", ",");
    return `${euros}€`;
  };

  const isSubscriptionRow = (row) => {
    const label = row
      .querySelector(SELECTORS.subLabel)
      ?.textContent?.trim()
      ?.toLowerCase();

    if (label && label.includes("subscription")) return true;

    const href = row.querySelector(SELECTORS.titleLink)?.getAttribute("href") || "";
    return href.includes("selling_plan=");
  };

  const getRowLine = (row) => {
    const input = row.querySelector(SELECTORS.qtyInput);
    return input ? Number(input.dataset.index) : null;
  };

  const getRowVariantId = (row) => {
    const input = row.querySelector(SELECTORS.qtyInput);
    return input ? Number(input.dataset.quantityVariantId) : null;
  };

  const parseSellingPlanFromHref = (href) => {
    try {
      const u = new URL(href, window.location.origin);
      const sp = u.searchParams.get("selling_plan");
      return sp ? Number(sp) : null;
    } catch {
      const m = href.match(/selling_plan=(\d+)/);
      return m ? Number(m[1]) : null;
    }
  };

  const findSellingPlanOnThisRow = (row) => {
    const href = row.querySelector(SELECTORS.titleLink)?.getAttribute("href") || "";
    return parseSellingPlanFromHref(href);
  };

  const fetchCart = async () => {
    const r = await fetch(`${ROOT}cart.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error("cart.js failed");
    return r.json();
  };

  const fetchProductJson = async (handle) => {
    if (PRODUCT_JSON_CACHE_BY_HANDLE.has(handle)) {
      return PRODUCT_JSON_CACHE_BY_HANDLE.get(handle);
    }
    const r = await fetch(`${ROOT}products/${handle}.js`, { credentials: "same-origin" });
    if (!r.ok) throw new Error(`products/${handle}.js failed`);
    const json = await r.json();
    PRODUCT_JSON_CACHE_BY_HANDLE.set(handle, json);
    return json;
  };

  const pickPlanIdForVariant = (productJson, variantId) => {
    const v = (productJson?.variants || []).find((x) => Number(x.id) === Number(variantId));
    const allocs = v?.selling_plan_allocations || [];
    if (!allocs.length) return null;
    const first = allocs[0];
    return Number(first.selling_plan_id || first.selling_plan?.id || first.id);
  };

  const getPlanDeliveryLabel = ({ cartItem, productJson, planId, row }) => {
    const frUnit = (interval, n) => {
      switch (interval) {
        case "day": return n === 1 ? "jour" : "jours";
        case "week": return n === 1 ? "semaine" : "semaines";
        case "month": return "mois";
        case "year": return n === 1 ? "an" : "ans";
        default: return "mois";
      }
    };

    const build = (interval, count) => {
      const n = Number(count || 1) || 1;
      const unit = frUnit(interval, n);
      return n === 1 ? `/ ${unit}` : `/ ${n} ${unit}`;
    };

    const tryPolicy = (sellingPlanObj) => {
      const policy = sellingPlanObj?.delivery_policy || sellingPlanObj?.billing_policy;
      const interval = policy?.interval;
      const count = policy?.interval_count;
      if (interval && Number(count || 1)) return { interval, count: Number(count || 1) };
      return null;
    };

    const sp = cartItem?.selling_plan_allocation?.selling_plan;
    const p1 = tryPolicy(sp);
    if (p1 && p1.count > 1) return build(p1.interval, p1.count);

    if (productJson && planId) {
      const groups = productJson?.selling_plan_groups || [];
      let found = null;
      for (const g of groups) {
        const plans = g?.selling_plans || [];
        found = plans.find((p) => Number(p.id) === Number(planId));
        if (found) break;
      }
      const p2 = tryPolicy(found);
      if (p2 && p2.count > 1) return build(p2.interval, p2.count);
    }

    const rawLabel =
      row?.querySelector(SELECTORS.subLabel)?.textContent?.trim()?.toLowerCase() || "";

    let m = rawLabel.match(/(\d+)\s*(month|mois|week|semaine|day|jour|year|an)/i);
    if (m) {
      const n = Number(m[1] || 1) || 1;
      const unit = (m[2] || "").toLowerCase();
      if (unit.startsWith("week") || unit.startsWith("semaine")) return build("week", n);
      if (unit.startsWith("day") || unit.startsWith("jour")) return build("day", n);
      if (unit.startsWith("year") || unit.startsWith("an")) return build("year", n);
      return build("month", n);
    }

    m = rawLabel.match(/tous\s+les\s+(\d+)\s*(jours|semaines|mois|ans)/i);
    if (m) {
      const n = Number(m[1] || 1) || 1;
      const unit = (m[2] || "").toLowerCase();
      if (unit.startsWith("semaine")) return build("week", n);
      if (unit.startsWith("jour")) return build("day", n);
      if (unit.startsWith("an")) return build("year", n);
      return build("month", n);
    }

    if (p1?.interval) return build(p1.interval, p1.count);
    return "/ mois";
  };

  const computeSubscriptionDeal = (productJson, variantId, planId) => {
    if (!productJson || !variantId || !planId) return null;

    const v = (productJson.variants || []).find((x) => Number(x.id) === Number(variantId));
    if (!v) return null;

    const basePrice = Number(v.price);
    if (!Number.isFinite(basePrice) || basePrice <= 0) return null;

    const allocs = v.selling_plan_allocations || [];
    if (!allocs.length) return null;

    const alloc = allocs.find((a) => Number(a.selling_plan_id) === Number(planId)) || allocs[0];
    if (!alloc) return null;

    const directPrice =
      (Number.isFinite(Number(alloc.price)) ? Number(alloc.price) : null) ??
      (Number.isFinite(Number(alloc.per_delivery_price)) ? Number(alloc.per_delivery_price) : null);

    if (directPrice && directPrice > 0) {
      const percent = Math.round((1 - directPrice / basePrice) * 100);
      return { basePrice, subPrice: directPrice, percent: Math.max(0, percent) };
    }

    const adjustments =
      alloc.price_adjustments ||
      alloc.price_adjustment ||
      alloc.price_adjustments_by_delivery ||
      [];

    const adj = Array.isArray(adjustments) ? adjustments[0] : adjustments;
    if (!adj) return null;

    const valueType = adj.value_type || adj.type;
    const value = Number(adj.value);

    let subPrice = basePrice;
    if (valueType === "percentage" && Number.isFinite(value)) {
      subPrice = Math.round(basePrice * (1 - value / 100));
    } else if (valueType === "fixed_amount" && Number.isFinite(value)) {
      subPrice = basePrice - value;
    } else if (valueType === "price" && Number.isFinite(value)) {
      subPrice = value;
    }

    if (!Number.isFinite(subPrice) || subPrice <= 0) return null;

    const percent = Math.round((1 - subPrice / basePrice) * 100);
    return { basePrice, subPrice, percent: Math.max(0, percent) };
  };

  const getPlanIdForLine = async ({ drawerEl, cart, line, variantId }) => {
    if (PLAN_CACHE_BY_VARIANT.has(variantId)) return PLAN_CACHE_BY_VARIANT.get(variantId);

    const rows = [...drawerEl.querySelectorAll(SELECTORS.row)];
    for (const r of rows) {
      if (!isSubscriptionRow(r)) continue;
      const v = getRowVariantId(r);
      if (v !== variantId) continue;
      const planId = findSellingPlanOnThisRow(r);
      if (planId) {
        PLAN_CACHE_BY_VARIANT.set(variantId, planId);
        return planId;
      }
    }

    const item = cart?.items?.[line - 1];
    const handle = item?.handle;
    if (!handle) return null;

    const productJson = await fetchProductJson(handle);
    const planId = pickPlanIdForVariant(productJson, variantId);
    if (planId) PLAN_CACHE_BY_VARIANT.set(variantId, planId);
    return planId;
  };

  // -----------------------------
  // injectSwitch — injecte dans .moodz-cart-card__switch-zone (div natif, full-width)
  // -----------------------------
  const injectSwitch = (row, { isSub, planId, dealText, deal, deliveryLabel, qty }) => {
    const line = getRowLine(row);
    if (!line) return;

    // Trouver la zone d'injection native dans la carte
    const switchZone = row.querySelector(SELECTORS.switchZone);
    if (!switchZone) return;

    // Guard : déjà injecté
    if (switchZone.querySelector(".moodz-sub-switch")) return;

    const labelOff = "S'abonner ?";
    const labelOn  = "Abonnement activé";

    let badgeHtml = "";
    if (deal) {
      const subTotal = deal.subPrice * qty;
      const subPriceTxt = `${formatMoney(subTotal)} ${deliveryLabel}`;
      badgeHtml = `
        <span class="moodz-switch__badge" aria-hidden="true">
          <span class="moodz-switch__badge-price">${subPriceTxt}</span>
        </span>`;
    }

    // Conteneur du switch
    const root = document.createElement("div");
    root.className = "moodz-sub-switch";
    root.dataset.line     = String(line);
    root.dataset.variantId = String(getRowVariantId(row));
    root.dataset.planId   = String(planId);

    root.innerHTML = `
      <label class="moodz-switch">
        <input type="checkbox" class="moodz-switch__input" ${isSub ? "checked" : ""}>
        <span class="moodz-switch__track" aria-hidden="true"></span>
        <span class="moodz-switch__label">
          <span class="moodz-switch__off">${labelOff}</span>
          <span class="moodz-switch__on">${labelOn}</span>
        </span>
                  ${deal.percent > 0 ? `<span class="moodz-switch__badge-saving">-${deal.percent}%</span>` : ""}
        ${badgeHtml}
      </label>
      ${dealText && !deal ? `<div class="moodz-switch__info">${dealText}</div>` : ""}
    `;

    // Injecter directement dans la zone prévue
    switchZone.appendChild(root);

    // Activer la bordure de séparation uniquement si injecté
    switchZone.style.display = "block";
  };

  // -----------------------------
  // changeLineSellingPlan
  // -----------------------------
  const changeLineSellingPlan = async ({ line, quantity, sellingPlanId }) => {
    const payload = {
      line,
      quantity,
      selling_plan: sellingPlanId ?? null,
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname,
    };

    const r = await fetch(`${ROOT}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    if (!r.ok) throw new Error("cart/change.js failed");
    const text = await r.text();
    try { return JSON.parse(text); } catch { return null; }
  };

  const refreshDrawerFallback = async () => {
    const cartDrawerContainer = document.querySelector("cart-drawer");
    if (!cartDrawerContainer) return;

    const routesObj = window.routes || window.Shopify?.routes;
    const cartUrl = routesObj?.cart_url || "/cart";

    const res = await fetch(cartUrl + "?view=drawer", { credentials: "same-origin" });
    if (!res.ok) throw new Error("drawer view refresh failed");

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newCartDrawer = doc.querySelector("#CartDrawer");
    if (!newCartDrawer) throw new Error("new #CartDrawer not found");

    const oldCartDrawer = cartDrawerContainer.querySelector("#CartDrawer") || cartDrawerContainer;
    if (window.cartFunctions?.updateCartDrawer) {
      window.cartFunctions.updateCartDrawer(oldCartDrawer, newCartDrawer, false);
    } else {
      oldCartDrawer.innerHTML = newCartDrawer.innerHTML;
    }
  };

  const applySectionsIfPossible = async (state) => {
    const html = state?.sections?.["cart-drawer"];
    if (!html) return false;

    const cartDrawerContainer = document.querySelector("cart-drawer");
    if (!cartDrawerContainer) return false;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const newCartDrawer = doc.querySelector("#CartDrawer");
    if (!newCartDrawer) return false;

    const oldCartDrawer = cartDrawerContainer.querySelector("#CartDrawer") || cartDrawerContainer;
    if (window.cartFunctions?.updateCartDrawer) {
      window.cartFunctions.updateCartDrawer(oldCartDrawer, newCartDrawer, false);
      return true;
    }
    oldCartDrawer.innerHTML = newCartDrawer.innerHTML;
    return true;
  };

  // -----------------------------
  // Enhance
  // -----------------------------
  let enhanceRunning = false;
  const enhance = async (drawerEl) => {
    if (enhanceRunning) return;
    enhanceRunning = true;

    try {
      const cart = await fetchCart();
      const rows = [...drawerEl.querySelectorAll(SELECTORS.row)];

      for (const row of rows) {
        const line = getRowLine(row);
        const variantId = getRowVariantId(row);
        if (!line || !variantId) continue;

        const item = cart?.items?.[line - 1];
        const handle = item?.handle || null;
        const qty = Number(item?.quantity || 1);
        const sub = isSubscriptionRow(row);

        let planId = null;
        if (sub) {
          const planFromHref = findSellingPlanOnThisRow(row);
          planId = planFromHref || (await getPlanIdForLine({ drawerEl, cart, line, variantId }));
          if (planId) PLAN_CACHE_BY_VARIANT.set(variantId, planId);
        } else {
          planId = await getPlanIdForLine({ drawerEl, cart, line, variantId });
        }

        if (!planId) continue;

        let dealText = "";
        let deal = null;
        let deliveryLabel = "";

        if (handle) {
          try {
            const productJson = await fetchProductJson(handle);
            deal = computeSubscriptionDeal(productJson, variantId, planId);
            deliveryLabel = getPlanDeliveryLabel({ cartItem: item, productJson, planId, row });

            if (deal) {
              const subTotal = deal.subPrice * qty;
              const subPriceTxt = `${formatMoney(subTotal)} ${deliveryLabel}`;
              if (!sub) {
                dealText = deal.percent > 0
                  ? `Économisez ${deal.percent}% • ${subPriceTxt}`
                  : `Prix abonnement • ${subPriceTxt}`;
              } else {
                dealText = deal.percent > 0
                  ? `Abonnement actif • -${deal.percent}% • ${subPriceTxt}`
                  : `Abonnement actif • ${subPriceTxt}`;
              }
            }
          } catch {}
        }

        injectSwitch(row, { isSub: sub, planId, dealText, deal, deliveryLabel, qty });
      }
    } catch (e) {
      console.error("[MOODZ] enhance error:", e);
    } finally {
      enhanceRunning = false;
    }
  };

  // -----------------------------
  // Switch handler
  // -----------------------------
  const onSwitchChange = async (e) => {
    const input = e.target?.closest?.(".moodz-switch__input");
    if (!input) return;

    const container = input.closest(".moodz-sub-switch");
    if (!container) return;

    const line   = Number(container.dataset.line);
    const planId = container.dataset.planId ? Number(container.dataset.planId) : null;
    const toSub  = input.checked;

    if (toSub && !planId) { input.checked = false; return; }

    input.disabled = true;
    container.style.opacity = "0.6";

    try {
      const qtyEl =
        document.querySelector(`#Drawer-quantity-${line}`) ||
        document.querySelector(`#Quantity-${line}`);
      const qty = qtyEl ? Number(qtyEl.value || "1") : 1;

      const state = await changeLineSellingPlan({
        line,
        quantity: qty,
        sellingPlanId: toSub ? planId : null,
      });

      const applied = await applySectionsIfPossible(state);
      if (!applied) await refreshDrawerFallback();
    } catch (err) {
      console.error("[MOODZ] switch error:", err);
      input.checked = !toSub;
      await refreshDrawerFallback().catch(() => {});
    } finally {
      input.disabled = false;
      container.style.opacity = "1";
    }
  };

  // -----------------------------
  // Init
  // -----------------------------
  const init = () => {
    if (window.__MOODZ_SUB_SWITCH_INIT__) return;
    window.__MOODZ_SUB_SWITCH_INIT__ = true;

    document.addEventListener("change", onSwitchChange);

    const run = () => {
      const drawer = document.querySelector(SELECTORS.drawer);
      if (drawer) enhance(drawer);
    };

    run();

    let rafId = null;
    const obs = new MutationObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(run);
    });

    obs.observe(document.body, { childList: true, subtree: true });
  };

  init();
})();